import React, { useState, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import { cn } from '@/lib/utils';

const normalizeSearchText = (text) =>
  String(text ?? '').toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * Searchable dropdown with optional "Others" choice.
 * options: Array<{ value: string, label: string }>
 * value: current selected value (or '__other__' when "Others" is selected)
 * onChange: (value: string) => void
 * allowOther: if true, adds "Others" option; when selected, onChange('__other__')
 */
export function SearchableSelect({
  options = [],
  value = '',
  onChange,
  placeholder = 'Select...',
  allowOther = false,
  otherLabel = 'Others',
  disabled = false,
  className = '',
  error = false,
  'data-testid': dataTestid,
}) {
  const [open, setOpen] = useState(false);
  const contentRef = useRef(null);
  const displayOptions = allowOther
    ? [...options, { value: '__other__', label: otherLabel }]
    : options;
  const selectedLabel = displayOptions.find((o) => o.value === value)?.label ?? (value && value !== '__other__' ? value : '');

  const handlePointerDownOutside = (e) => {
    if (contentRef.current && contentRef.current.contains(e.target)) {
      e.preventDefault();
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={dataTestid}
          disabled={disabled}
          className={cn(
            'flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors',
            'placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-gray-800 focus:border-gray-400',
            'data-[state=open]:border-gray-400 data-[state=open]:ring-1 data-[state=open]:ring-gray-800 data-[state=open]:outline-none',
            'disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
            error && 'border-red-500',
            className
          )}
        >
          <span className={cn('min-w-0 flex-1 truncate text-left', !selectedLabel ? 'text-muted-foreground' : '')}>
            {selectedLabel || placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        className="w-[var(--radix-popover-trigger-width)] min-w-[16rem] max-w-[24rem] p-0 rounded-lg shadow-lg border border-border bg-popover"
        align="start"
        onPointerDownOutside={handlePointerDownOutside}
        onInteractOutside={(e) => {
          if (contentRef.current && contentRef.current.contains(e.target)) {
            e.preventDefault();
          }
        }}
      >
        <Command
          shouldFilter={true}
          filter={(value, search, keywords = []) => {
            const query = normalizeSearchText(search);
            if (!query) return 1;

            const haystack = normalizeSearchText([value, ...keywords].join(' '));
            return haystack.includes(query) ? 1 : 0;
          }}
          className="rounded-lg overflow-hidden"
        >
          <CommandInput placeholder="Search..." />
          <CommandList className="max-h-[300px] overscroll-contain touch-pan-y">
            <CommandEmpty>No result found.</CommandEmpty>
            <CommandGroup>
              {displayOptions.map((opt, index) => (
                <CommandItem
                  key={`${opt.value}-${index}`}
                  value={opt.value}
                  keywords={[opt.label]}
                  onSelect={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                >
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
